import React, { useState } from "react";
import { Briefcase, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Container, Icon, PageHero, Reveal, SectionHeading } from "../components/shared/Shared";
import { submitApplication, getErrorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const ApplyDialog = ({ job, onClose }) => {
  const { t, isRTL } = useLang();
  const k = t.careers;
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", cover_letter: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitApplication({ ...form, position: job.title });
      setDone(true);
      toast.success(k.toast);
    } catch (err) {
      toast.error(getErrorMessage(err, t.common.error));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={!!job} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="apply-dialog" dir={isRTL ? "rtl" : "ltr"}>
        {done ? (
          <div className="flex flex-col items-center py-8 text-center" data-testid="apply-success">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></span>
            <h3 className="mt-5 font-heading text-2xl font-bold text-brand-navy">{k.received}</h3>
            <p className="mt-2 text-slate-600">{k.thanks(job?.title)}</p>
            <button type="button" onClick={onClose} className="btn-primary mt-8 bg-brand-teal text-white hover:bg-brand-teal-dark">{k.close}</button>
          </div>
        ) : (
          <>
            <DialogHeader className={isRTL ? "text-start sm:text-start" : ""}>
              <DialogTitle className="font-heading text-2xl text-brand-navy">{k.applyFor} {job?.title}</DialogTitle>
              <DialogDescription>{job?.location} &middot; {job?.type}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid gap-4" data-testid="apply-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="a-name">{k.name}</Label><Input id="a-name" required value={form.name} onChange={set("name")} data-testid="apply-name-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="a-email">{k.email}</Label><Input id="a-email" type="email" required value={form.email} onChange={set("email")} data-testid="apply-email-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="a-phone">{k.phone}</Label><Input id="a-phone" value={form.phone} onChange={set("phone")} /></div>
                <div className="space-y-1.5"><Label htmlFor="a-linkedin">{k.linkedin}</Label><Input id="a-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="https://" /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="a-cover">{k.cover}</Label><Textarea id="a-cover" rows={4} value={form.cover_letter} onChange={set("cover_letter")} placeholder={k.coverPh} /></div>
              <button type="submit" disabled={loading} className="btn-primary bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60" data-testid="apply-submit-button">{loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {k.submitting}</> : k.submit}</button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Careers = () => {
  const { t, c } = useLang();
  const k = t.careers;
  const [job, setJob] = useState(null);
  return (
    <>
      <PageHero title={k.title} subtitle={k.subtitle} crumbs={[{ label: k.title }]} image={c.IMAGES.careers} />
      <section className="py-24" data-testid="career-enablement">
        <Container>
          <SectionHeading eyebrow={k.ceEyebrow} title={k.ceTitle} align="center" />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {c.CAREER_ENABLEMENT.map((x, i) => (
              <Reveal key={x.title} delay={i * 100} className="card-hover rounded-3xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange"><Icon name={x.icon} className="h-6 w-6" /></span>
                <h3 className="mt-6 font-heading text-xl font-bold text-brand-navy">{x.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{x.description}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      <section className="bg-slate-50 py-24" data-testid="life-at-section">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow={k.lifeEyebrow} title={k.lifeTitle} description={k.lifeDesc} />
            <Reveal delay={100} className="mt-8 overflow-hidden rounded-3xl shadow-xl shadow-brand-navy/10"><img src={c.IMAGES.team} alt="Life at Jonaren" className="aspect-[4/3] w-full object-cover" /></Reveal>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {c.LIFE_AT.map((l, i) => (
              <Reveal key={l.title} delay={i * 70} className="card-hover rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal"><Icon name={l.icon} className="h-5 w-5" /></span>
                <h3 className="mt-4 font-heading text-lg font-bold text-brand-navy">{l.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{l.description}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      <section className="py-24" data-testid="openings-section">
        <Container>
          <SectionHeading eyebrow={k.openingsEyebrow} title={k.openingsTitle} />
          <div className="mt-12 grid gap-4">
            {c.OPENINGS.map((o, i) => (
              <Reveal key={o.title} delay={i * 60} className="card-hover flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 md:flex-row md:items-center md:justify-between" data-testid="job-card">
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-brand-navy">{o.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-xs font-medium text-slate-500"><span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-brand-teal" />{o.location}</span><span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5 text-brand-teal" />{o.type}</span></div>
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{o.description}</p>
                </div>
                <button type="button" onClick={() => setJob(o)} className="btn-primary shrink-0 bg-brand-teal text-white hover:bg-brand-teal-dark" data-testid="apply-button">{k.apply}</button>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      <ApplyDialog job={job} onClose={() => setJob(null)} />
    </>
  );
};
